/**
 * SchemeDiversityView.jsx — Orchestrator
 *
 * Receives pid + viewIds from SchemeDiversity (parent).
 * Renders the 4 gated sections by importing dedicated sub-components:
 *
 *   view 10 → <DiversityInSchemes />      (big filter table)
 *   view 11 → <SchemeDocumentRatio />     (BR/DS/Open tabs)
 *   view 12 → <SchemeRatioOverview />     (BR/DS/Open tabs)
 *   view 13 → <CitizenStateWiseStats />   (citizen stats table)
 *
 * VIEW_IDS:
 *   DIVERSITY_IN_SCHEMES    = 10
 *   DOCUMENT_SCHEME_RATIO   = 11
 *   SCHEME_RATIO_OVERVIEW   = 12
 *   CITIZEN_STATEWISE_STATS = 13
 */

import React from 'react';
import { VIEW_IDS } from '../../constants/ViewIds';
import { useState } from 'react';
import DiversityInSchemes         from './DiversityInSchemes';
import SchemeDocumentRatio from './SchemeDocumentRatio';
import SchemeDiversityBRTable from './SchemeDiversityBRTable';
import SchemeDiversityDSTable from './DSTable';
import SchemeDiversityTable from "./SchemeDiversityTable";
import SchemeDocumentRatioDS from './SchemeDocumentRationDS';
import SchemeDocumentRatioOpen from './SchemeDocumentRationOpen';
import CitizenStatsTable from "../Project Level/CitizenStatsTable"
import SchemeWiseStatistics from './SchemeWiseStatistics';

const SchemeDiversityView = ({ pid, viewIds = [], isSuperuser = false }) => {
  const [activeTab, setActiveTab] = useState('BR');
  const [view, setView] = useState('br');
  const hasView = (id) => isSuperuser || viewIds.includes(id);

  const hasAnything = (
    hasView(VIEW_IDS.DIVERSITY_IN_SCHEMES)    ||
    hasView(VIEW_IDS.DOCUMENT_SCHEME_RATIO)   ||
    hasView(VIEW_IDS.SCHEME_RATIO_OVERVIEW)   ||
    hasView(VIEW_IDS.CITIZEN_STATEWISE_STATS) || 
    hasView(VIEW_IDS.SCHEME_WISE_STATISTICS)
  );

  if (!hasAnything) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-gray-400">
        <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M12 15v2m0 0v2m0-2h2m-2 0H10M9 7h1m-1 4h1m4-4h1m-1 4h1M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <p className="text-lg font-medium">No views configured for this project.</p>
      </div>
    );
  }

  return (
    <div>
      {/* view 10 — Diversity in Schemes */}
      {hasView(VIEW_IDS.DIVERSITY_IN_SCHEMES) && (
        <DiversityInSchemes pid={pid} />
      )}

      {hasView(VIEW_IDS.SCHEME_WISE_STATISTICS) && <SchemeWiseStatistics/>}
      {hasView(VIEW_IDS.DOCUMENT_SCHEME_RATIO) && (
        <div className="border max-w-[92vw] border-gray-200 rounded-md ml-[3vw] p-4 my-4 shadow-md shadow-black">
          <div className="flex mb-4">
            <h3 className="text-xl font-bold text-pink-800">Scheme VS Document Ratio</h3>
            <div className="flex space-x-3 ml-[20vw]">
              <button onClick={() => setActiveTab('BR')}   className={`px-4 py-2 rounded-md ${activeTab === 'BR'   ? 'text-pink-800 bg-pink-300' : 'bg-gray-100 hover:bg-gray-200'}`}>BR</button>
              <button onClick={() => setActiveTab('DS')}   className={`px-4 py-2 rounded-md ${activeTab === 'DS'   ? 'text-pink-800 bg-pink-300' : 'bg-gray-100 hover:bg-gray-200'}`}>DS</button>
              <button onClick={() => setActiveTab('Open')} className={`px-4 py-2 rounded-md ${activeTab === 'Open' ? 'text-pink-800 bg-pink-300' : 'bg-gray-100 hover:bg-gray-200'}`}>Open</button>
            </div>
          </div>
          <div>
            {activeTab === 'BR'   && <SchemeDocumentRatio />}
            {activeTab === 'DS'   && <SchemeDocumentRatioDS />}
            {activeTab === 'Open' && <SchemeDocumentRatioOpen />}
          </div>
        </div>
      )}

      {/* ── SCHEME RATIO OVERVIEW — view_id 12  (was: roleId !== 28) ─────── */}
      {hasView(VIEW_IDS.SCHEME_RATIO_OVERVIEW) && (
        <div className="border max-w-[92vw] border-gray-200 rounded-md ml-[3vw] p-4 my-4 shadow-md shadow-black">
          <div className="flex mb-4">
            <h3 className="text-xl font-bold text-pink-600">Scheme Ratio Overview</h3>
            <div className="flex space-x-3 ml-[20vw]">
              <button onClick={() => setView('br')}   className={`px-4 py-2 rounded-md text-sm font-medium transition ${view === 'br'   ? 'text-pink-800 bg-pink-300' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}>BR</button>
              <button onClick={() => setView('ds')}   className={`px-4 py-2 rounded-md text-sm font-medium transition ${view === 'ds'   ? 'text-pink-800 bg-pink-300' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}>DS</button>
              <button onClick={() => setView('open')} className={`px-4 py-2 rounded-md text-sm font-medium transition ${view === 'open' ? 'text-pink-800 bg-pink-300' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}>Open</button>
            </div>
          </div>
          {view === 'br'   && <SchemeDiversityBRTable />}
          {view === 'ds'   && <SchemeDiversityDSTable />}
          {view === 'open' && <SchemeDiversityTable />}
        </div>
      )}

      {/* ── CITIZEN STATE WISE STATISTICS — view_id 13 ────────────────────── */}
      {hasView(VIEW_IDS.CITIZEN_STATEWISE_STATS) && (
        <div>
          <CitizenStatsTable />
        </div>
      )}
    </div>
  );
};

export default SchemeDiversityView;